// @flow
import { FormBuilderContext } from 'pages/Contexts/FormBuilderContext'
import * as React from 'react'
import Icon, { IconSources } from 'ui/atoms/Icon'
import Icons from 'ui/Icons'
import {
  CONSTANTS_FORM_BUILDER,
  SELECTION_TYPE,
} from 'utils/FormBuilder/ConstantsFormBuilder'
import { ZentakeContext } from 'utils/ZentakeContext'

import BuilderPopupFragment from './BuilderPopupFragments'

type Props = {
  hasMakeBiggerOption: ?boolean,
  hasMakeSmallerOption: ?boolean,
  hasMoveDown: ?boolean,
  hasMoveUp: ?boolean,
  id: string,
  onCloseRequest: (id: string) => void,
  type: string,
}

function BuilderPopupQuestion({
  hasMakeBiggerOption,
  hasMakeSmallerOption,
  hasMoveDown,
  hasMoveUp,
  id,
  onCloseRequest,
  type,
}: Props): React.Node {
  const { setAlertConfig } = React.useContext(ZentakeContext)
  const {
    deletePage,
    deleteQuestion,
    deleteSection,
    duplicateQuestion,
    getQuestion,
    isEditingDescription,
    onMovePageDown,
    onMovePageUp,
    questionsArray,
    setQuestion,
    setSelectedItem,
    updateMultipleQuestions,
  } = React.useContext(FormBuilderContext)

  const onDuplicateQuestion = React.useCallback(() => {
    const newQuestionId = duplicateQuestion(id)
    setSelectedItem({
      type: CONSTANTS_FORM_BUILDER.SELECTION_TYPE.QUESTION,
      id: newQuestionId,
    })
  }, [duplicateQuestion, id, setSelectedItem])

  const onDeleteClick = React.useCallback(async () => {
    switch (type) {
      case SELECTION_TYPE.QUESTION:
        await deleteQuestion(id)
        return onCloseRequest(id)
      case SELECTION_TYPE.SECTION:
        setAlertConfig({
          title: 'Are you sure you want to delete this section?',
          description:
            'By confirming, the section and its questions will be deleted permanently.',
          buttons: [
            {
              title: 'Delete Section',
              style: 'destructive',
              onPress: () => {
                deleteSection(id)
              },
            },
            {
              title: 'Cancel',
              onPress: () => {},
            },
          ],
          canDismiss: false,
        })
        return onCloseRequest(id)
      case SELECTION_TYPE.PAGE:
        setAlertConfig({
          title: 'Are you sure you want to delete this section?',
          description:
            'By confirming, the section and its questions will be deleted permanently.',
          buttons: [
            {
              title: 'Delete section',
              style: 'destructive',
              onPress: () => {
                deletePage(id)
              },
            },
            {
              title: 'Cancel',
              onPress: () => {},
            },
          ],
          canDismiss: false,
        })
        return onCloseRequest(id)
      default:
        break
    }
  }, [
    id,
    type,
    onCloseRequest,
    deleteQuestion,
    deleteSection,
    deletePage,
    setAlertConfig,
  ])

  const onMakeBigger = React.useCallback(
    (e) => {
      e.stopPropagation()
      const question = getQuestion?.(id)
      const nextQuestion = {
        ...question,
        display_columns: question?.display_columns + 1,
      }
      setQuestion?.(id, nextQuestion)
      // onCloseRequest()
    },
    [id, getQuestion, setQuestion]
  )

  const onMakeSmaller = React.useCallback(
    (e) => {
      e.stopPropagation()
      const question = getQuestion?.(id)
      const nextQuestion = {
        ...question,
        display_columns: question?.display_columns - 1,
      }
      setQuestion?.(id, nextQuestion)
      // onCloseRequest()
    },
    [id, getQuestion, setQuestion]
  )

  const onBalance = React.useCallback(
    (e) => {
      e.stopPropagation()
      const question = getQuestion(id)
      const questionsInRow = questionsArray.filter(
        (_question) => _question.row === question.row
      )
      const balancedColumns = Math.floor(12 / questionsInRow.length)
      const lastColumn = 12 - balancedColumns * (questionsInRow.length - 1)

      const updatedQuestions = questionsInRow.map((_question, index) => {
        return {
          ..._question,
          display_columns:
            index === questionsInRow.length - 1 ? lastColumn : balancedColumns,
        }
      })
      updateMultipleQuestions(updatedQuestions)
    },
    [getQuestion, id, questionsArray, updateMultipleQuestions]
  )

  const onMoveUp = React.useCallback(
    (e) => {
      e.stopPropagation()
      const currentPageId = id
      setSelectedItem()
      onMovePageUp(id)
      setTimeout(() => {
        setSelectedItem({ id: currentPageId, type: SELECTION_TYPE.PAGE })
      })
    },
    [id, onMovePageUp, setSelectedItem]
  )

  const onMoveDown = React.useCallback(
    (e) => {
      e.stopPropagation()
      const currentPageId = id
      setSelectedItem()
      onMovePageDown(id)
      setTimeout(() => {
        setSelectedItem({ id: currentPageId, type: SELECTION_TYPE.PAGE })
      })
    },
    [id, onMovePageDown, setSelectedItem]
  )

  const typesWithoutOptions = [
    SELECTION_TYPE.ROW_ADD,
    SELECTION_TYPE.QUESTION_ADD,
    SELECTION_TYPE.FORM,
  ]

  if (typesWithoutOptions.includes(type)) {
    return null
  }

  // Hide the popup when edit description question modal is on, so they won't overlap
  if (isEditingDescription) return null

  return (
    <BuilderPopupFragment.PopupContainer>
      <BuilderPopupFragment.IconButton
        IconComponent={Icons.Trash}
        onClick={onDeleteClick}
      >
        Delete
      </BuilderPopupFragment.IconButton>
      {type === SELECTION_TYPE.QUESTION && (
        <BuilderPopupFragment.IconButton
          IconComponent={Icons.Duplicate}
          onClick={onDuplicateQuestion}
        >
          Duplicate
        </BuilderPopupFragment.IconButton>
      )}
      {type === SELECTION_TYPE.QUESTION && (
        <>
          <BuilderPopupFragment.IconButton
            IconComponent={() => <Icon name={IconSources.BALANCE} size={16} />}
            onClick={onBalance}
          >
            Balance
          </BuilderPopupFragment.IconButton>
          {hasMakeSmallerOption && (
            <BuilderPopupFragment.IconButton
              IconComponent={Icons.ArrowLeftSmall}
              onClick={onMakeSmaller}
            >
              Shrink
            </BuilderPopupFragment.IconButton>
          )}
          {hasMakeBiggerOption && (
            <BuilderPopupFragment.IconButton
              IconComponent={Icons.ArrowRightSmall}
              onClick={onMakeBigger}
            >
              Grow
            </BuilderPopupFragment.IconButton>
          )}
        </>
      )}
      {type === SELECTION_TYPE.PAGE && (
        <>
          {hasMoveUp && (
            <BuilderPopupFragment.IconButton
              IconComponent={Icons.ArrowUpSmall}
              onClick={onMoveUp}
            >
              Move up
            </BuilderPopupFragment.IconButton>
          )}
          {hasMoveDown && (
            <BuilderPopupFragment.IconButton
              IconComponent={Icons.ArrowDownSmall}
              onClick={onMoveDown}
            >
              Move down
            </BuilderPopupFragment.IconButton>
          )}
        </>
      )}
    </BuilderPopupFragment.PopupContainer>
  )
}

export default BuilderPopupQuestion
